import React, { memo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";

// Ein Knoten des Werks-Instanz-Mappings (Zielinstanz links, Werk rechts) als MUI Paper mit data-card-id.
// Bedienung läuft delegiert über die Zeichenfläche (PlantInstanceMapping); der Knoten selbst trägt nur Daten und Zustand
// als Attribute. Damit bleiben die Props flach und vergleichbar: bei einer Kantenänderung rendern nur Knoten neu, deren
// Werkszahl sich ändert. Alle sx-Objekte sind Konstanten.
const accentDark = { '[data-theme="dark"] &': { borderColor: "var(--iio-info)" } };
export const nodeSx = {
  display: "flex", alignItems: "stretch", gap: 1, width: "100%", m: 0, p: 0,
  border: "1px solid var(--iio-border)", borderRadius: "6px", boxShadow: "none", backgroundImage: "none",
  background: "var(--iio-bg-2)", backgroundColor: "var(--iio-bg-2)",
  color: "var(--iio-text-1)", font: "inherit", fontSize: 13, textAlign: "left", userSelect: "none",
  // Der Knoten lässt senkrechtes Scrollen per Touch zu; gezogen wird auf Touch nur am Griff (touch-action: none).
  touchAction: "pan-y",
  transition: "border-color 120ms ease, background-color 120ms ease",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  "&[data-pending='true']": { borderStyle: "dashed" },
  "&[data-interactive='true']": { cursor: "grab" },
  "&[data-interactive='true']:hover": { borderColor: "var(--iio-brand-ink)", ...accentDark },
  "&:focus-visible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: 1 },
  "&[aria-pressed='true'], &[data-drop='true']": {
    borderColor: "var(--iio-brand-ink)", ...accentDark,
    background: "color-mix(in srgb, var(--iio-brand-ink) 10%, var(--iio-bg-2))",
  },
  "&:disabled": { cursor: "not-allowed", opacity: .7 },
};
const handleSx = {
  display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", width: 24,
  color: "var(--iio-text-3)", touchAction: "none", cursor: "grab",
  borderRight: "1px solid var(--iio-border-subtle)",
  "& svg": { fontSize: 18 },
};
const bodySx = { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, flex: 1, px: 1.5, py: 1 };
const nameSx = { fontFamily: "inherit", fontSize: 13, fontWeight: 600, overflowWrap: "anywhere", lineHeight: 1.35 };
const metaSx = { fontFamily: "inherit", fontSize: 12, color: "var(--iio-text-2)", overflowWrap: "anywhere" };
const defaultSx = { ...metaSx, fontWeight: 600, color: "var(--iio-brand-ink)", '[data-theme="dark"] &': { color: "var(--iio-info)" } };
const pendingSx = { ...metaSx, fontWeight: 500, color: "var(--iio-warning)" };
const countSx = { ...metaSx, color: "var(--iio-text-3)" };

/**
 * Props: cardId, kind ("instance" | "plant"), kennung, stage, targetId, title, meta, isDefault, pending, count (Zahl oder null),
 * picked, editable (Schaltfläche), disabled (gesperrt, z. B. beim Speichern), ariaLabel, t.
 */
export function MapNodeView({ cardId, kind, kennung, stage, targetId, title, meta, isDefault, pending, count, picked, editable, disabled, ariaLabel, t }) {
  const interactive = editable && !disabled;
  const own = editable
    ? { component: "button", type: "button", "aria-pressed": picked, "aria-label": ariaLabel, disabled }
    : { component: "div" };
  return (
    <Paper {...own} sx={nodeSx} data-card-id={cardId} data-map-node={kind} data-kennung={kennung} data-stage={stage} data-target-id={targetId}
      data-pending={pending ? "true" : undefined} data-interactive={interactive ? "true" : undefined}>
      {interactive && <Box component="span" sx={handleSx} data-drag-handle="true" aria-hidden="true"><DragIndicatorRounded/></Box>}
      <Box component="span" sx={bodySx}>
        <Typography component="span" sx={nameSx}>{title}</Typography>
        {meta && <Typography component="span" sx={metaSx}>{meta}</Typography>}
        {isDefault && <Typography component="span" sx={defaultSx}>{t("plantMapping.isDefault")}</Typography>}
        {pending && <Typography component="span" sx={pendingSx}>{t("plantMapping.notConfigured")}</Typography>}
        {count !== null && count !== undefined && <Typography component="span" sx={countSx}>{t("plantMapping.plantCount", { count })}</Typography>}
      </Box>
    </Paper>
  );
}

export const MapNode = memo(MapNodeView);
